/* =============================================================
   App.js — Root component: routing and context providers
   -------------------------------------------------------------
   Sets up the three global context providers (Auth, Document,
   Sidebar) and defines all client-side routes for both the
   admin portal and the student portal.

   Route structure:
     /login                    — shared login page
     /dashboard, /incoming…    — admin screens (role: admin)
     /student, /student/…      — student screens (role: student)

   ✓ DONE (backend integration):
   - Added ProtectedRoute wrapper: checks auth state and role
     before rendering any screen, redirects if not authorised
   - Added LoginRoute guard: redirects logged-in users away
     from /login to their correct portal
   - Added LoadingSpinner: shown while Supabase checks the
     existing session on page load (prevents login-page flash)
   ============================================================= */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SidebarProvider }  from './context/SidebarContext';
import { DocumentProvider } from './context/DocumentContext';
import { AuthProvider }     from './context/AuthContext';
import { useAuth }          from './context/AuthContext';

/* -------------------------------------------------------------
   LOGIN
   ------------------------------------------------------------- */
import Login from './screens/Login';

/* -------------------------------------------------------------
   ADMIN SCREENS
   ------------------------------------------------------------- */
import Dashboard              from './screens/Dashboard';
import IncomingList           from './screens/IncomingList';
import IncomingProfile        from './screens/IncomingProfile';
import OutgoingList           from './screens/OutgoingList';
import OutgoingProfile        from './screens/OutgoingProfile';
import GenericIncomingProfile from './screens/GenericIncomingProfile';
import GenericOutgoingProfile from './screens/GenericOutgoingProfile';
import AIAssistant            from './screens/AIAssistant';
import PartnersList           from './screens/PartnersList';
import PartnerProfile         from './screens/PartnerProfile';
import Settings               from './screens/Settings';
import Analytics              from './screens/Analytics';
import PartnerExchangeHistory from './screens/PartnerExchangeHistory';

/* -------------------------------------------------------------
   STUDENT SCREENS
   ------------------------------------------------------------- */
import StudentDashboard   from './screens/student/StudentDashboard';
import StudentUniversity  from './screens/student/StudentUniversity';
import StudentDocuments   from './screens/student/StudentDocuments';
import StudentExperiences from './screens/student/StudentExperiences';
import StudentAIAssistant from './screens/student/StudentAIAssistant';
import StudentSettings    from './screens/student/StudentSettings';
import StudentDiscounts   from './screens/student/StudentDiscounts';


/* -------------------------------------------------------------
   MOBILE BLOCK
   Shown on screens narrower than 1024px (tablets and phones).
   The admin portal is desktop-only; the student portal will be
   made responsive in a future release.
   ------------------------------------------------------------- */
function MobileBlock() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2ff] px-8 text-center">
      <div className="bg-white rounded-3xl shadow-lg px-10 py-12 max-w-sm w-full flex flex-col items-center gap-6">
        {/* Logo */}
        <img src="/logo.jpg" alt="Unixplore" className="w-16 h-16 rounded-2xl shadow-sm" />
        {/* Icon */}
        <span className="material-symbols-outlined text-[48px] text-indigo-300">desktop_windows</span>
        {/* Text */}
        <div>
          <h1 className="font-display text-xl font-bold text-[#0b1c30] mb-2">
            Open on a computer
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Unixplore is optimised for desktop.<br />
            Please open it from a laptop or PC.
          </p>
        </div>
        {/* Brand */}
        <p className="text-xs text-slate-300 mt-2">Unixplore · Exchange Made Effortless</p>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------
   LOADING SPINNER
   Shown while Supabase checks for an existing session on page
   load. Prevents the app from flashing the login page for a
   split second before confirming the user is already logged in.
   ------------------------------------------------------------- */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}


/* -------------------------------------------------------------
   PROTECTED ROUTE
   Wraps every screen that requires authentication.
   - While loading  → show spinner
   - Not logged in  → redirect to /login
   - Wrong role     → redirect to the user's correct portal
   - Correct role   → render the screen normally

   Usage:
     <ProtectedRoute requiredRole="admin">
       <Dashboard />
     </ProtectedRoute>
   ------------------------------------------------------------- */
function ProtectedRoute({ children, requiredRole }) {
  const { authUser, user, loading } = useAuth();
  const location = useLocation();

  // Phase 1: Supabase checking localStorage for a session (< 100 ms).
  if (loading) return <LoadingSpinner />;

  // No session at all — send to login.
  // authUser comes from localStorage, not the network, so this is
  // reliable even after a tab freeze or slow network.
  if (!authUser) return <Navigate to="/login" replace />;

  // Phase 2: session confirmed, profile still loading from DB.
  // Show a brief spinner rather than redirecting anywhere.
  if (!user) return <LoadingSpinner />;

  // Admin preview mode: allow admins to visit student routes when
  // the ?as=<studentId> query param is present in the URL.
  const isAdminPreview =
    user.role === 'admin' &&
    requiredRole === 'student' &&
    new URLSearchParams(location.search).has('as');

  // User is logged in but has the wrong role —
  // redirect to their own portal instead of showing an error.
  if (requiredRole && user.role !== requiredRole && !isAdminPreview) {
    return <Navigate to={user.role === 'admin' ? '/dashboard' : '/student'} replace />;
  }

  // All checks passed — render the requested screen.
  return children;
}


/* -------------------------------------------------------------
   LOGIN ROUTE
   Protects the /login page in the opposite direction:
   if the user is already logged in, skip login and send them
   straight to their portal.
   ------------------------------------------------------------- */
function LoginRoute() {
  const { authUser, user, loading } = useAuth();

  // Phase 1: checking session.
  if (loading) return <LoadingSpinner />;

  // Session exists but profile still loading — spinner, not login page.
  if (authUser && !user) return <LoadingSpinner />;

  // Already logged in — redirect to the correct portal.
  if (user) {
    return <Navigate to={user.role === 'admin' ? '/dashboard' : '/student'} replace />;
  }

  return <Login />;
}


/* -------------------------------------------------------------
   APP
   Context providers wrap the entire route tree so every screen
   can access auth state, document state, and sidebar state.
   ------------------------------------------------------------- */
export default function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) return <MobileBlock />;

  return (
    <BrowserRouter>
      <AuthProvider>
      <DocumentProvider>
      <SidebarProvider>
        <Routes>

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Login — guarded: redirects if already logged in */}
          <Route path="/login" element={<LoginRoute />} />

          {/* ── Admin routes ── */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="admin"><Dashboard /></ProtectedRoute>
          }/>
          <Route path="/incoming" element={
            <ProtectedRoute requiredRole="admin"><IncomingList /></ProtectedRoute>
          }/>
          <Route path="/incoming/carlos-mendez" element={
            <ProtectedRoute requiredRole="admin"><IncomingProfile /></ProtectedRoute>
          }/>
          <Route path="/incoming/:id" element={
            <ProtectedRoute requiredRole="admin"><GenericIncomingProfile /></ProtectedRoute>
          }/>
          <Route path="/outgoing" element={
            <ProtectedRoute requiredRole="admin"><OutgoingList /></ProtectedRoute>
          }/>
          <Route path="/outgoing/tom-baker" element={
            <ProtectedRoute requiredRole="admin"><OutgoingProfile /></ProtectedRoute>
          }/>
          <Route path="/outgoing/:id" element={
            <ProtectedRoute requiredRole="admin"><GenericOutgoingProfile /></ProtectedRoute>
          }/>
          <Route path="/ai-assistant" element={
            <ProtectedRoute requiredRole="admin"><AIAssistant /></ProtectedRoute>
          }/>
          <Route path="/partners" element={
            <ProtectedRoute requiredRole="admin"><PartnersList /></ProtectedRoute>
          }/>
          <Route path="/partners/:id" element={
            <ProtectedRoute requiredRole="admin"><PartnerProfile /></ProtectedRoute>
          }/>
          <Route path="/settings" element={
            <ProtectedRoute requiredRole="admin"><Settings /></ProtectedRoute>
          }/>
          <Route path="/analytics" element={
            <ProtectedRoute requiredRole="admin"><Analytics /></ProtectedRoute>
          }/>
          <Route path="/analytics/partner/:id" element={
            <ProtectedRoute requiredRole="admin"><PartnerExchangeHistory /></ProtectedRoute>
          }/>

          {/* ── Student routes ── */}
          <Route path="/student" element={
            <ProtectedRoute requiredRole="student"><StudentDashboard /></ProtectedRoute>
          }/>
          <Route path="/student/university" element={
            <ProtectedRoute requiredRole="student"><StudentUniversity /></ProtectedRoute>
          }/>
          <Route path="/student/documents" element={
            <ProtectedRoute requiredRole="student"><StudentDocuments /></ProtectedRoute>
          }/>
          <Route path="/student/experiences" element={
            <ProtectedRoute requiredRole="student"><StudentExperiences /></ProtectedRoute>
          }/>
          <Route path="/student/discounts" element={
            <ProtectedRoute requiredRole="student"><StudentDiscounts /></ProtectedRoute>
          }/>
          <Route path="/student/ai-assistant" element={
            <ProtectedRoute requiredRole="student"><StudentAIAssistant /></ProtectedRoute>
          }/>
          <Route path="/student/settings" element={
            <ProtectedRoute requiredRole="student"><StudentSettings /></ProtectedRoute>
          }/>

        </Routes>
      </SidebarProvider>
      </DocumentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
